import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../../services/api';
import { useAuth } from '../../hooks/AuthContext';
import { ErrorBox } from '../../components/Ui';
import { useTitle } from '../../hooks';
import { AuthFrame } from '../../design-system/components';
import { PasswordField } from '../../components/auth/PasswordField';
import { CountryField } from '../../components/auth/CountryField';

import { safeNext } from '../../shared/utils/auth';
export function Login() {
  useTitle('Sign in');
  const { user, signIn } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const next = safeNext(params.get('next'));

  if (user) return <Navigate to={next} replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await signIn(username.trim(), password);
      navigate(next, { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame>
      <div>
        <header className="auth-form-heading">
          <h1>Sign in</h1>
          <p>Continue to your Library workspace.</p>
        </header>
        <form className="auth-form" onSubmit={submit}>
          {error && <ErrorBox error={error} />}
          <div className="field">
            <label htmlFor="username">Username or email</label>
            <input
              id="username"
              className="input"
              autoComplete="username"
              autoFocus
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <PasswordField value={password} onChange={setPassword} />
          <button className="btn primary full-width" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <p className="muted auth-footnote">
          New here?{' '}
          <Link to={`/register${params.get('next') ? `?next=${encodeURIComponent(next)}` : ''}`}>
            Create an account
          </Link>
          .
        </p>
      </div>
    </AuthFrame>
  );
}
